import { createBrowserRouter, Navigate } from "react-router-dom";
import { pageTitleFor } from "./pageTitle.js";
import { Login } from "./personas/Login.js";
import { PublicJobs } from "./personas/public/PublicJobs.js";
import { PublicJob } from "./personas/public/PublicJob.js";
import { Signup } from "./personas/Signup.js";
import { VerifySignup } from "./personas/VerifySignup.js";
import { ApproveSignup } from "./personas/ApproveSignup.js";
import { SetPassword } from "./personas/SetPassword.js";
import { ForgotPassword } from "./personas/ForgotPassword.js";
import { AdminApp, AdminHome } from "./personas/admin/AdminApp.js";
import { Overview } from "./personas/admin/Overview.js";
import { Providers } from "./personas/admin/Providers.js";
import { InviteModes } from "./personas/admin/InviteModes.js";
import { ClientOrgs } from "./personas/admin/ClientOrgs.js";
import { SignupRequests } from "./personas/admin/SignupRequests.js";
import { OrgDetail } from "./personas/admin/OrgDetail.js";
import { Spend } from "./personas/admin/Spend.js";
import { Regions } from "./personas/admin/Regions.js";
import { Models } from "./personas/admin/Models.js";
import { ModelPrompts } from "./personas/admin/ModelPrompts.js";
import { AiModels } from "./personas/admin/AiModels.js";
import { EmailTemplates } from "./personas/admin/EmailTemplates.js";
import { AdminTeam } from "./personas/admin/AdminTeam.js";
import { Audit } from "./personas/admin/Audit.js";
import { DeadLetters } from "./personas/admin/DeadLetters.js";
import { ApiErrors } from "./personas/admin/ApiErrors.js";
import { Feedback } from "./personas/admin/Feedback.js";
import { InterviewLanguages } from "./personas/admin/InterviewLanguages.js";
import { Compliance } from "./personas/admin/Compliance.js";
import { Payments } from "./personas/admin/Payments.js";
import { Releases } from "./personas/admin/Releases.js";
import { Revenue } from "./personas/admin/Revenue.js";
import { Sourcing } from "./personas/admin/Sourcing.js";
import { AccountSecurity } from "./personas/AccountSecurity.js";
import { Profile } from "./personas/Profile.js";
import { UsersApp } from "./personas/users/UsersApp.js";
import { DashboardHome } from "./personas/users/DashboardHome.js";
import { NewRequest } from "./personas/users/NewRequest.js";
import { Requisitions } from "./personas/users/Requisitions.js";
import { RequisitionDetail } from "./personas/users/RequisitionDetail.js";
import { Review } from "./personas/users/Review.js";
import { Plans } from "./personas/users/Plans.js";
import { CreditPricing } from "./personas/admin/CreditPricing.js";
import { PlansAdmin } from "./personas/admin/PlansAdmin.js";
import { Partners } from "./personas/admin/Partners.js";
import { Verticals } from "./personas/admin/Verticals.js";
import { Taxonomy } from "./personas/admin/Taxonomy.js";
import { SpeechQuality } from "./personas/admin/SpeechQuality.js";
import { JudgeCalls } from "./personas/admin/JudgeCalls.js";
import { Connectors } from "./personas/admin/Connectors.js";
import { PartnerEconomy, PartnerOrgs } from "./personas/users/Partner.js";
import { Team } from "./personas/users/Team.js";
import { CandidateApp } from "./personas/candidate/CandidateApp.js";
import { FindMyLink } from "./personas/candidate/FindMyLink.js";
import { CompleteProfile } from "./personas/candidate/CompleteProfile.js";
import { SpeechCheck } from "./personas/speechcheck/SpeechCheck.js";
import { SharedReport } from "./personas/SharedReport.js";
import { ResumesHub } from "./personas/users/ResumesHub.js";
import { Connections } from "./personas/users/Connections.js";
import { Inbox } from "./personas/users/Inbox.js";
import { ActivityPage } from "./personas/users/ActivityPage.js";
import { InterviewsHub } from "./personas/users/InterviewsHub.js";
import { RecruitmentNetwork } from "./personas/users/network/RecruitmentNetwork.js";
import { RequireAuth, RootRedirect } from "./components/RequireAuth.js";

export const router = createBrowserRouter([
  // "/" → persona home if signed in, else /login.
  { path: "/", element: <RootRedirect /> },
  { path: "/login", element: <Login /> },

  // PUBLIC self-serve onboarding (0069) — free (admin-approved) or paid (pay-to-activate).
  { path: "/signup", element: <Signup /> },
  { path: "/verify-signup", element: <VerifySignup /> },
  // Super Admin's emailed Approve button (0110) — authorised by its single-use link token.
  { path: "/approve-signup", element: <ApproveSignup /> },

  // PUBLIC self-service auth — one-time links + non-enumerating recovery. No auth gate.
  // /set-password (account invite) and /reset-password (forgot flow) share one component.
  { path: "/set-password", element: <SetPassword /> },
  { path: "/reset-password", element: <SetPassword /> },
  { path: "/forgot-password", element: <ForgotPassword /> },

  // ADMIN persona — TalentHop owner + platform team. Auth-gated to the admin persona.
  {
    path: "/admin",
    element: (
      <RequireAuth persona="admin">
        <AdminApp />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <AdminHome /> },
      { path: "plans", element: <PlansAdmin /> },
      { path: "credit-pricing", element: <CreditPricing /> },
      { path: "spend", element: <Spend /> },
      { path: "billing", element: <Revenue /> },
      { path: "payments", element: <Payments /> },
      { path: "orgs", element: <ClientOrgs /> },
      { path: "signup-requests", element: <SignupRequests /> },
      { path: "partners", element: <Partners /> },
      { path: "orgs/:orgId", element: <OrgDetail /> },
      { path: "sourcing", element: <Sourcing /> },
      { path: "regions", element: <Regions /> },
      { path: "verticals", element: <Verticals /> },
      { path: "taxonomy", element: <Taxonomy /> },
      { path: "speech-quality", element: <SpeechQuality /> },
      { path: "interview-languages", element: <InterviewLanguages /> },
      { path: "models", element: <Models /> },
      { path: "prompts", element: <ModelPrompts /> },
      { path: "ai-models", element: <AiModels /> },
      { path: "judge-calls", element: <JudgeCalls /> },
      { path: "email-templates", element: <EmailTemplates /> },
      { path: "providers", element: <Providers /> },
      { path: "connectors", element: <Connectors /> },
      { path: "invite-modes", element: <InviteModes /> },
      { path: "releases", element: <Releases /> },
      { path: "team", element: <AdminTeam /> },
      { path: "audit", element: <Audit /> },
      { path: "dead-letters", element: <DeadLetters /> },
      { path: "api-errors", element: <ApiErrors /> },
      { path: "feedback", element: <Feedback /> },
      { path: "compliance", element: <Compliance /> },
      { path: "security", element: <AccountSecurity /> },
    ],
  },

  // USERS persona — a client hiring org. Auth-gated to the user persona.
  {
    path: "/app",
    element: (
      <RequireAuth persona="user">
        <UsersApp />
      </RequireAuth>
    ),
    children: [
      // /app opens the saved view: Network (default) or Classic — see DashboardHome.tsx.
      { index: true, element: <DashboardHome /> },
      { path: "new", element: <NewRequest /> },
      { path: "score", element: <Navigate to="/app/new" replace /> },
      { path: "requisitions", element: <Requisitions /> },
      { path: "requisitions/:id", element: <RequisitionDetail /> },
      { path: "candidates", element: <ResumesHub /> },
      { path: "resumes", element: <ResumesHub /> },
      { path: "interviews", element: <InterviewsHub /> },
      { path: "inbox", element: <Inbox /> },
      // 0112: the complete activity history behind the top-bar bell.
      { path: "activity", element: <ActivityPage /> },
      // Recruitment Network (Network View). Classic Dashboard is the other option at /app.
      { path: "network", element: <RecruitmentNetwork /> },
      { path: "connections", element: <Connections /> },
      { path: "funnel", element: <Navigate to="/app/requisitions" replace /> },
      { path: "review", element: <Navigate to="/app/requisitions" replace /> },
      { path: "review/:candidacyId", element: <Review /> },
      // 0094: plans replace the customer-facing credits page; old links land on Plans & usage.
      { path: "billing", element: <Navigate to="/app/plans" replace /> },
      { path: "credits", element: <Navigate to="/app/plans" replace /> },
      { path: "plans", element: <Plans /> },
      { path: "partner", element: <PartnerEconomy /> },
      { path: "partner/orgs", element: <PartnerOrgs /> },
      { path: "plan", element: <Navigate to="/app/plans" replace /> },
      { path: "team", element: <Team /> },
      { path: "profile", element: <Profile /> },
      { path: "security", element: <AccountSecurity /> },
    ],
  },

  // CANDIDATE persona — capability-token journey (public; server-state-driven).
  { path: "/interview/:token", element: <CandidateApp /> },
  { path: "/interview", element: <CandidateApp /> },
  { path: "/find-my-link", element: <FindMyLink /> },

  // PUBLIC "complete your application" — capability token, no login (migration 0085). A separate
  // token from the interview invite: this grants sending details and a CV, nothing more.
  { path: "/complete-profile/:token", element: <CompleteProfile /> },

  // PUBLIC job pages — opt-in per requirement, no login. /jobs = every open shared role,
  // /careers/:company = one company, /jobs/:slug = one role (live only while it is open).
  { path: "/jobs", element: <PublicJobs /> },
  { path: "/jobs/:slug", element: <PublicJob /> },
  { path: "/careers/:companySlug", element: <PublicJobs /> },

  // PUBLIC shared report — capability token, redaction-aware, read-only.
  { path: "/shared/:token", element: <SharedReport /> },

  // PUBLIC ASR speech-check harness — volunteers open a shared link (?tag=pilot1) on their own
  // phones, no account. Measures OUR Web Speech transcription quality on Indian English before
  // the pilot; text only, no audio ever recorded. See docs/SPEECH_CHECK_PROTOCOL.md.
  { path: "/speech-check", element: <SpeechCheck /> },

  { path: "*", element: <Navigate to="/" replace /> },
]);

// Tab title per page ("TalentHop - Candidates") — set now for the landing route, then on every
// navigation. One subscription here instead of an effect in each page (see pageTitle.ts).
document.title = pageTitleFor(router.state.location.pathname);
router.subscribe((state) => {
  document.title = pageTitleFor(state.location.pathname);
});
